import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { authAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import { 
  Wifi, 
  Clock, 
  HardDrive, 
  LogOut, 
  Activity, 
  CheckCircle,
  AlertCircle,
  Download
} from 'lucide-react';


const UserDashboard = () => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  const { logout } = useAuth();
  const navigate = useNavigate();

  const totalBytesUsed = session?.bytes_used ?? 0;
  const voucherBytesUsed = session?.voucher_bytes_used ?? 0;
  const sessionBytesUsed = session?.session_bytes_used ?? 0;
  const bytesQuota = session?.bytes_quota ?? null;
  const remainingBytes = session?.remaining_bytes ?? (bytesQuota ? Math.max(0, bytesQuota - totalBytesUsed) : null);

  useEffect(() => {
    loadSession();
    
    // Refresh session data every 30 seconds
    const interval = setInterval(loadSession, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (session) {
      updateTimeRemaining();
      const interval = setInterval(updateTimeRemaining, 1000);
      return () => clearInterval(interval);
    }
  }, [session]);

  const loadSession = async () => {
    try {
      const response = await authAPI.getSession();
      setSession(response.data.session);
    } catch (error) {
      console.error('Failed to load session:', error);
      if (error.response?.status === 404) {
        toast.error('No active session found');
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const updateTimeRemaining = () => {
    if (!session) return;
    
    const now = new Date();
    const expires = new Date(session.expires_at);
    const remaining = Math.max(0, Math.floor((expires - now) / 1000));
    
    setTimeRemaining(remaining);
    
    if (remaining === 0) {
      toast.error('Your session has expired');
      handleLogout();
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect?')) {
      return;
    }

    try {
      await authAPI.disconnect();
      toast.success('Disconnected successfully');
      handleLogout();
    } catch (error) {
      console.error('Disconnect error:', error);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      navigate('/');
    }
  };

  const formatTime = (seconds) => {
    if (seconds <= 0) return '00:00:00';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getUsagePercentage = () => {
    if (!bytesQuota) return 0;
    return Math.min((totalBytesUsed / bytesQuota) * 100, 100);
  };

  const getTimePercentage = () => {
    if (!session) return 0;
    const total = new Date(session.expires_at) - new Date(session.start_at || Date.now());
    const used = total - (timeRemaining * 1000);
    return Math.min((used / total) * 100, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading your session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 left-10 w-96 h-96 bg-green-500 opacity-10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-500 opacity-10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-purple-500 opacity-5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-6 md:p-8 mb-8 shadow-2xl">
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-6 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-gradient-to-br from-green-400/20 to-blue-400/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 relative">
                <img 
                  src="/logo.png" 
                  alt="Sateva Hotspot" 
                  className="h-10 w-10 object-contain drop-shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <CheckCircle className="h-8 w-8 text-green-400 absolute drop-shadow-lg" style={{display: 'none'}} />
              </div>
              <div className="text-center lg:text-left">
                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white via-green-200 to-blue-200 bg-clip-text text-transparent">
                  Sateva Hotspot
                </h1>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <p className="text-white/70">Welcome {session?.user_name}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-3 px-4 py-2 bg-green-500/20 rounded-full border border-green-400/30">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-green-200 font-medium">Connected</span>
              </div>
              <button
                onClick={handleDisconnect}
                className="bg-gradient-to-r from-red-500/80 to-pink-500/80 hover:from-red-600 hover:to-pink-600 backdrop-blur-sm border border-red-400/30 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-2"
              >
                <LogOut className="h-4 w-4" />
                <span>Disconnect</span>
              </button>
            </div>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
          {/* Plan Info */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-400/20 rounded-2xl p-6 hover:from-blue-500/20 hover:to-purple-500/20 transition-all duration-300 group">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-400/30">
                <Wifi className="h-6 w-6 text-blue-300 group-hover:scale-110 transition-transform" />
              </div>
              <h2 className="text-lg font-semibold text-white">Current Plan</h2>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-blue-300 mb-2">{session?.plan}</p>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <p className="text-white/60 text-sm font-mono break-all">
                {session?.client_ip}
              </p>
            </div>
          </div>

          {/* Time Remaining */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-400/20 rounded-2xl p-6 hover:from-orange-500/20 hover:to-red-500/20 transition-all duration-300 group">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-orange-500/20 rounded-xl border border-orange-400/30">
                <Clock className="h-6 w-6 text-orange-300 group-hover:scale-110 transition-transform" />
              </div>
              <h2 className="text-lg font-semibold text-white">Time Left</h2>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-orange-300 font-mono mb-3">
              {formatTime(timeRemaining)}
            </p>
            <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-orange-400 to-orange-500 h-3 rounded-full transition-all duration-1000 shadow-lg"
                style={{ width: `${100 - getTimePercentage()}%` }}
              ></div>
            </div>
            <p className="text-white/50 text-xs mt-2">
              {Math.round(100 - getTimePercentage())}% remaining
            </p>
          </div>

          {/* Data Usage */}
          <div className="backdrop-blur-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-400/20 rounded-2xl p-6 hover:from-purple-500/20 hover:to-pink-500/20 transition-all duration-300 group md:col-span-2 xl:col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-purple-500/20 rounded-xl border border-purple-400/30">
                <HardDrive className="h-6 w-6 text-purple-300 group-hover:scale-110 transition-transform" />
              </div>
              <h2 className="text-lg font-semibold text-white">Data Used</h2>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-purple-300 mb-2">
              {formatBytes(totalBytesUsed)}
            </p>
            {bytesQuota && (
              <>
                <p className="text-white/60 text-sm mb-3">
                  of {formatBytes(bytesQuota)} total
                </p>
                <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-purple-400 to-pink-500 h-3 rounded-full transition-all duration-1000 shadow-lg"
                    style={{ width: `${getUsagePercentage()}%` }}
                  ></div>
                </div>
                <p className="text-white/50 text-xs mt-2">
                  {Math.round(getUsagePercentage())}% used
                </p>
                {(voucherBytesUsed > 0 || sessionBytesUsed > 0) && (
                  <div className="text-white/60 text-xs mt-2 space-y-1">
                    {voucherBytesUsed > 0 && (
                      <p>Previous sessions: {formatBytes(voucherBytesUsed)}</p>
                    )}
                    {sessionBytesUsed > 0 && (
                      <p>Current session: {formatBytes(sessionBytesUsed)}</p>
                    )}
                    {remainingBytes != null && (
                      <p className="text-white/70">
                        Remaining: {formatBytes(remainingBytes)}
                      </p>
                    )}
                  </div>
                )}
                {session?.voucher_remaining_bytes != null && (
                  <p className="text-white/60 text-xs mt-1">
                    {formatBytes(session.voucher_remaining_bytes)} remaining in voucher pool
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Connection Status */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center space-x-4 p-4 bg-green-500/10 rounded-xl border border-green-400/20">
              <div className="p-3 bg-green-500/20 rounded-xl">
                <div className="h-4 w-4 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
              </div>
              <div>
                <p className="text-green-300 font-semibold">VPN Connected</p>
                <p className="text-white/60 text-sm">Secure tunnel active</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 p-4 bg-blue-500/10 rounded-xl border border-blue-400/20">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <Activity className="h-4 w-4 text-blue-300" />
              </div>
              <div>
                <p className="text-blue-300 font-semibold">Traffic Active</p>
                <p className="text-white/60 text-sm">Data flowing normally</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 p-4 bg-purple-500/10 rounded-xl border border-purple-400/20">
              <div className="p-3 bg-purple-500/20 rounded-xl">
                <CheckCircle className="h-4 w-4 text-purple-300" />
              </div>
              <div>
                <p className="text-purple-300 font-semibold">Session Valid</p>
                <p className="text-white/60 text-sm">Access authorized</p>
              </div>
            </div>
          </div>
        </div>

        {/* Warnings */}
        {(timeRemaining < 300 || (session?.bytes_quota && getUsagePercentage() > 80)) && (
          <div className="backdrop-blur-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-400/30 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-3 bg-amber-500/20 rounded-xl border border-amber-400/30">
                <AlertCircle className="h-6 w-6 text-amber-300 animate-pulse" />
              </div>
              <h3 className="text-lg font-semibold text-amber-200">Attention Required</h3>
            </div>
            <div className="space-y-3">
              {timeRemaining < 300 && (
                <div className="flex items-center space-x-3 p-3 bg-red-500/10 rounded-xl border border-red-400/20">
                  <Clock className="h-5 w-5 text-red-300 flex-shrink-0" />
                  <p className="text-red-200">Your session will expire in less than 5 minutes</p>
                </div>
              )}
              {session?.bytes_quota && getUsagePercentage() > 80 && (
                <div className="flex items-center space-x-3 p-3 bg-orange-500/10 rounded-xl border border-orange-400/20">
                  <HardDrive className="h-5 w-5 text-orange-300 flex-shrink-0" />
                  <p className="text-orange-200">You have used more than 80% of your data quota</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
